import React, { useEffect, useState, useMemo, useCallback } from "react";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import {
  captchaCode,
  captchaCodePlaceholder,
  captchaSubHeader,
  carrierRateMsg,
  invalidCaptchaCode,
  loadingMsg,
  mobile,
  otpNotReceived,
  otpWillExpireMsg,
  resend,
  speed,
  submit,
  twoFactorVerificationMessage,
  verifyingHuman,
  verifyMobileHeader,
  verifyMobileSubHeader,
} from "../messages";
import {
  callAPIInterface,
  captchaBackgroundColor,
  captchaDigit,
  getCountryObj,
  getCurrentTimeInSecond,
  handleAfterLoginProcess,
  mobileNumberWithoutCallingCode,
  validatePhoneNumber,
} from "../constants";
import Button from "../button/Button";
import Text from "../text/Text";
import { Box } from "@mui/material";
import PhoneNumberInput from "../phoneNumberInput/PhoneNumberInput";
import { useFormik } from "formik";
import { mixed, object } from "yup";
import { OTPInput } from "../otpInput/OTPInput";
import { CustomLink } from "../link/Link";
import { useSession } from "../../hooks/useSession";
import { useLocation, useNavigate } from "react-router-dom";
import CommonHeader from "../CommonHeader";
import {
  LoadCanvasTemplate,
  loadCaptchaEnginge,
  validateCaptcha,
} from "react-simple-captcha";
import { Input } from "../input/Input";
import { sessionService } from "redux-react-session";

// Custom hook for OTP timer functionality
const useOtpTimer = () => {
  const [timerCount, setTimerCount] = useState(0);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState();

  const setTimerStates = useCallback((timerExpireAt) => {
    const currentSeconds = getCurrentTimeInSecond(timerExpireAt);
    setTimerCount(currentSeconds);
    currentSeconds === 0 && setIsOtpExpired(true);
  }, []);

  const resetTimer = useCallback((expiresAt) => {
    setIsOtpExpired(false);
    setTimerCount(getCurrentTimeInSecond(expiresAt));
    setOtpExpiresAt(expiresAt);
  }, []);

  useEffect(() => {
    if (otpExpiresAt && timerCount > 0) {
      const timer = setInterval(() => {
        setTimerStates(otpExpiresAt);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [otpExpiresAt, timerCount, setTimerStates]);

  return {
    timerCount,
    isOtpExpired,
    resetTimer,
  };
};

function PhoneNumberVerification({ isFromOptions }) {
  const [isLoading, setIsLoading] = useState(false);
  const [countryIsoCode, setCountryISOCode] = useState();
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [callingCodes, setCallingCodes] = useState([]);
  const [isInvalidCaptcha, setIsInvalidCaptcha] = useState(false);
  const [captcha, setCaptcha] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { timerCount, isOtpExpired, resetTimer } = useOtpTimer();

  const { session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const isFromVerifyEmailPage = location?.state?.isFromVerifyEmailPage;

  const isFromProfile = location?.state?.isFromProfile;

  const userCountry = session?.user?.country;

  const accountCountry = session?.account?.account?.country;

  const phoneNumberValidationSchema = object({
    mobileNo: mixed().test("mobileNo", validatePhoneNumber).required(""),
  });

  const phoneNumberFormik = useFormik({
    initialValues: {
      mobileDialCode: "",
      mobileNo: "",
    },
    validationSchema: phoneNumberValidationSchema,
  });

  const { values, setFieldValue, setTouched, errors, isValid, dirty, touched } =
    phoneNumberFormik;

  useEffect(() => {
    setTimeout(() => {
      loadCaptchaEnginge(captchaDigit, captchaBackgroundColor);
    }, 0);
  }, []);

  const handleSubmitPhoneNumber = async () => {
    try {
      setIsLoading(true);
      const apiData = {
        calling_code: values?.mobileDialCode,
        phone_number: mobileNumberWithoutCallingCode(
          values.mobileDialCode,
          values.mobileNo
        ),
      };

      const res = await callAPIInterface(
        "POST",
        "/profile/phone-number",
        JSON.stringify(apiData)
      );

      resetTimer(res?.otp_expires_at);
      setStep((prev) => prev + 1);
    } catch (error) {
      console.error("Error submitting phone number:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitOtp = async () => {
    try {
      setIsLoading(true);
      await callAPIInterface("POST", "/profile/phone-number/verify", { otp });
      if (isFromProfile) {
        const newUserData = {
          ...location?.state?.user,
          calling_code: values?.mobileDialCode,
          phone_number: mobileNumberWithoutCallingCode(
            values?.mobileDialCode,
            values?.mobileNo
          ),
        };
        await sessionService.saveSession({
          ...session,
          user: newUserData,
        });
        navigate("/profile");
      } else {
        await handleAfterLoginProcess(navigate);
      }
    } catch (error) {
      console.error("Error submitting OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCallingCodes = async () => {
    try {
      setIsLoading(true);
      const codes = await callAPIInterface("GET", "/mfa/calling-code");
      setCallingCodes(codes.country_calling_codes);
    } catch (error) {
      console.error("Error fetching calling codes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const res = await callAPIInterface(
        "POST",
        "/profile/phone-number/resend-verification-code"
      );
      resetTimer(res?.otp_expires_at);
    } catch (error) {
      console.error("Error resending OTP:", error);
    }
  };

  const handleSubmit = (currentStep) => {
    switch (currentStep) {
      case 1:
        handleVerifyCaptcha();
        break;
      case 2:
        handleSubmitPhoneNumber();
        break;
      case 3:
        handleSubmitOtp();
        break;
      default:
        break;
    }
  };

  const formattedTimerDisplay = useMemo(() => {
    if (timerCount <= 0) return null;
    const minutes = Math.floor(timerCount / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (timerCount % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  }, [timerCount]);

  const handleVerifyCaptcha = () => {
    setIsSubmitting(true);
    const isValid = validateCaptcha(captcha);

    if (isValid) {
      setStep((p) => p + 1);
      setIsInvalidCaptcha(false);
    } else {
      setIsInvalidCaptcha(true);
    }
    setIsSubmitting(false);
  };

  const enterCaptchaCode = useMemo(() => {
    return (
      <Box>
        <Text className="grey-text" size={14}>
          {captchaSubHeader}
        </Text>
        <Box margin="20px 0">
          <LoadCanvasTemplate />
        </Box>
        <Input
          fullWidth
          showLabel
          value={captcha}
          label={captchaCode}
          onChange={(e) => {
            setIsInvalidCaptcha(false);
            setCaptcha(e.target.value);
          }}
          type="text"
          inputProps={{ maxLength: 6 }}
          customClass="mt-24"
          placeholder={captchaCodePlaceholder}
        />
        {isInvalidCaptcha && (
          <Text
            className="error-text"
            size={16}
            font="regular"
            sx={{ textAlign: "left", marginTop: "5px" }}
          >
            {invalidCaptchaCode}
          </Text>
        )}
      </Box>
    );
  }, [isInvalidCaptcha, captcha, isSubmitting]);

  const phoneNumberInput = useMemo(
    () => (
      <>
        <Text className="grey-text mt-8" size={14}>
          {verifyMobileSubHeader}
        </Text>
        <PhoneNumberInput
          onOpen={() => {
            document.body.style.overflow = "hidden";
          }}
          onClose={() => {
            document.body.style.overflow = "auto";
          }}
          name="mobileNo"
          options={callingCodes}
          error={touched?.mobileNo && Boolean(errors?.mobileNo)}
          selectValue={values.mobileDialCode}
          MenuProps={{
            id: "verify-phone-number-calling-code",
            disableScrollLock: true,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            PaperProps: {
              style: {
                maxHeight: 300,
                width: 200,
              },
            },
          }}
          countryIsoCode={countryIsoCode}
          inputValue={values.mobileNo}
          label={mobile}
          showLabel={true}
          onChangeSelect={(value) => {
            setFieldValue("mobileNo", "");
            setFieldValue(
              "mobileDialCode",
              typeof value === "object" ? value.calling_code : value
            );
          }}
          onBlur={() => setTouched({ ...touched, mobileNo: true })}
          onChange={(value) => {
            setTouched({ ...touched, mobileNo: false });
            setFieldValue("mobileNo", value);
          }}
          boxClassName="mt-24"
        />
        <Text
          variant="subtitle1"
          font="regular"
          size={14}
          className="grey-text mt-8"
        >
          {carrierRateMsg}
        </Text>
      </>
    ),
    [
      values,
      touched,
      errors,
      callingCodes,
      countryIsoCode,
      setFieldValue,
      setTouched,
    ]
  );

  const otpInput = useMemo(
    () => (
      <>
        <Text className="grey-text mt-8" size={14}>
          <>
            {twoFactorVerificationMessage[0]}{" "}
            <span style={{ fontWeight: "bold" }}>
              {values?.mobileNo?.slice(-4)}.
            </span>
          </>
        </Text>
        <OTPInput
          value={otp}
          onChange={(val) => setOtp(val)}
          numInputs={6}
          containerStyle={{
            margin: "20px 0",
            gap: "3%",
          }}
        />
        {timerCount > 0 && !isOtpExpired && (
          <Text sx={{ color: "#667085" }} size={14} align="center">
            {otpWillExpireMsg}{" "}
            <Text sx={{ color: "#0A0A0A" }} component="span" font="bold">
              {formattedTimerDisplay}
            </Text>
          </Text>
        )}
        {isOtpExpired && (
          <Text
            className="default-text"
            size={14}
            align="center"
            sx={{ marginTop: "12px" }}
          >
            {otpNotReceived}{" "}
            <CustomLink bold onClick={handleResendOTP} size="small">
              {resend}
            </CustomLink>
          </Text>
        )}
      </>
    ),
    [otp, values, timerCount, isOtpExpired, formattedTimerDisplay]
  );

  const renderVerificationBody = useMemo(() => {
    switch (step) {
      case 1:
        return enterCaptchaCode;
      case 2:
        return phoneNumberInput;
      case 3:
        return otpInput;
      default:
        return null;
    }
  }, [step, phoneNumberInput, otpInput, enterCaptchaCode]);

  useEffect(() => {
    if (isFromVerifyEmailPage) setStep(2);
  }, []);

  useEffect(() => {
    // Set country code according to calling_code
    const countryObj = getCountryObj(
      "calling_code",
      values?.mobileDialCode,
      callingCodes
    );
    setCountryISOCode(countryObj?.iso2);
  }, [values?.mobileDialCode, callingCodes]);

  useEffect(() => {
    const accountCountryObj = getCountryObj(
      "short_name",
      accountCountry,
      callingCodes
    );
    if (accountCountryObj) {
      setCountryISOCode(accountCountryObj?.iso2);
      setFieldValue("mobileDialCode", accountCountryObj?.calling_code);
    }
  }, [accountCountry, callingCodes, setFieldValue]);

  useEffect(() => {
    if (step === 2) {
      getCallingCodes();
    }
  }, [step]);

  return (
    <>
      <BackdropLoader
        open={isLoading}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <CommonHeader
        icon="backArrow"
        textAfterIcon={speed}
        showSpeedIcon={true}
        navigation={isFromVerifyEmailPage && "/wallet"}
      />
      <Box className="options-container">
        <Box className="phone-number-verification-wrapper">
          <Box>
            <Text className="default-text" size={22} font="bold">
              {step === 1 ? verifyingHuman : verifyMobileHeader}
            </Text>
            {renderVerificationBody}
          </Box>
          <Box>
            <Button
              fullWidth
              variant="contained"
              label={submit}
              onClick={() => handleSubmit(step)}
              disabled={step === 1 ? !captcha : !(isValid && dirty)}
              {...(!isFromOptions && { textClass: "font-14" })}
              sx={{ marginTop: "20px" }}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default PhoneNumberVerification;
